/*
  Product bundle.

  Product columns with pill variant selectors and a details panel that shows
  what this section has added so far.

  It does not compute or display a discount anywhere. Discounting is Shopify
  automatic discounts applied at checkout, and whether one applies depends on
  the whole cart, so any "you save" figure shown here would be a guess the
  theme cannot honour.

  The bundle state is an array on the element. No localStorage, no
  sessionStorage: it describes what happened in this section on this page, and
  a stale list restored after a reload would be describing a cart that may
  since have changed.
*/
class ProductBundle extends HTMLElement {
  connectedCallback() {
    this.added = [];
    this.stateList = this.querySelector('[data-state-list]');
    this.stateEmpty = this.querySelector('[data-state-empty]');

    this.querySelectorAll('[data-product]').forEach((column) => this.setupColumn(column));
    this.renderState();
  }

  setupColumn(column) {
    const button = column.querySelector('[data-add]');
    const label = column.querySelector('[data-add-label]');
    if (!button) return;

    /*
      Sold out is decided in Liquid, from the product's availability at render
      time, and locks the column outright. There is nothing to choose.
    */
    if (column.dataset.soldOut === 'true') {
      button.disabled = true;
      label.textContent = button.dataset.labelSoldOut;
      return;
    }

    /*
      A product with only a default variant has no options to pick, so the
      button is live immediately rather than sitting on "Choose a variant
      first" with nothing to choose from.
    */
    if (column.dataset.variantId) {
      button.disabled = false;
      label.textContent = button.dataset.labelDefault;
    } else {
      this.setupOptions(column);
    }

    button.addEventListener('click', () => this.add(column));
  }

  setupOptions(column) {
    const script = column.querySelector('[data-variants]');
    if (!script) return;

    try {
      column.variants = JSON.parse(script.textContent);
    } catch (error) {
      console.error(error);
      return;
    }

    /*
      Pills are real buttons with aria-pressed rather than radios, because they
      are not part of a form here: the selection is resolved against the variant
      data above and posted as an id. One pressed pill per option row.
    */
    column.querySelectorAll('[data-option-value]').forEach((pill) => {
      pill.addEventListener('click', () => {
        const row = pill.closest('[data-option-index]');
        row.querySelectorAll('[data-option-value]').forEach((other) => {
          other.setAttribute('aria-pressed', String(other === pill));
        });
        this.resolve(column);
      });
    });
  }

  selection(column) {
    const rows = Array.from(column.querySelectorAll('[data-option-index]'));
    const values = rows.map((row) => row.querySelector('[aria-pressed="true"]')?.dataset.optionValue || null);
    return { values, complete: values.every(Boolean) };
  }

  /*
    A variant matches when every one of its options equals the pill chosen in
    the corresponding row. Comparing the whole set rather than the first option
    is what makes a two-option product work; picking only the first would
    silently add whichever variant happened to be listed first under it.
  */
  resolve(column) {
    const button = column.querySelector('[data-add]');
    const label = column.querySelector('[data-add-label]');
    const error = column.querySelector('[data-error]');
    const { values, complete } = this.selection(column);

    if (!complete) {
      column.selectedVariant = null;
      button.disabled = true;
      label.textContent = button.dataset.labelChoose;
      if (error) error.textContent = '';
      return;
    }

    const match = (column.variants || []).find((variant) =>
      values.every((value, index) => variant.options[index] === value)
    );

    column.selectedVariant = match && match.available ? match : null;

    if (!column.selectedVariant) {
      button.disabled = true;
      label.textContent = button.dataset.labelChoose;
      if (error) error.textContent = error.dataset.unavailableText;
      return;
    }

    button.disabled = false;
    label.textContent = button.dataset.labelDefault;
    if (error) error.textContent = '';
  }

  async add(column) {
    const button = column.querySelector('[data-add]');
    const error = column.querySelector('[data-error]');
    const variantId = column.dataset.variantId || column.selectedVariant?.id;
    if (!variantId) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    button.disabled = true;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    if (error) error.textContent = '';

    const body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement(document.activeElement);
    }

    let failed = false;
    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `Cart returned ${response.status}`);
      }

      this.added.push({
        title: column.dataset.productTitle,
        variant: column.selectedVariant ? column.selectedVariant.title : null,
      });
      this.renderState();

      if (cart) {
        cart.renderContents(data);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cartData: data });
        }
      } else {
        window.location = window.routes.cart_url;
        return;
      }
    } catch (caught) {
      console.error(caught);
      failed = true;
    } finally {
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
      button.disabled = false;
      /*
        Written after the button is restored rather than inside the catch, so
        nothing later in this block can clear it.
      */
      if (failed && error) error.textContent = error.dataset.errorText;
    }
  }

  renderState() {
    if (!this.stateList || !this.stateEmpty) return;

    const empty = this.added.length === 0;
    this.stateEmpty.hidden = !empty;
    this.stateList.hidden = empty;
    this.stateList.innerHTML = '';

    this.added.forEach((entry) => {
      const item = document.createElement('li');
      item.className = 'product-bundle__state-item';
      /*
        textContent, not innerHTML: these strings are product and variant
        titles from the store, and a title containing markup would otherwise be
        injected into the panel.
      */
      item.textContent = entry.variant ? `${entry.title} - ${entry.variant}` : entry.title;
      this.stateList.appendChild(item);
    });
  }
}

customElements.define('product-bundle', ProductBundle);
